/**
 * RV9a · the operation-type library.
 *
 * **137 types is a library. 138 is a fork.** An author who cannot tell whether an existing
 * type fits will make another one — which is how ~25,000 lines of forked report code
 * happened, one level up. So this page's job is not to list 137 rows. It is to make overlap
 * visible, and everything below serves that:
 *
 *   · coverage leads — a strip of cards across the top, one per thing wrong with the
 *     library, each a filter. What is *wrong* is the thing an author needs before adding
 *   · types are grouped by category, so "is there already one of these?" is answerable by
 *     looking at one section
 *   · each card says what the type is made of, what it expects, and what uses it — the three
 *     things that distinguish two types with similar names
 *
 * Nothing here is counted on the client. A coverage number computed from a page of rows
 * would be a number about that page, not about the library.
 */
import HeaderActions from '@/components/HeaderActions';
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import {
  Alert, Button, Card, Col, Descriptions, Empty, Flex, Input, Modal, Row, Segmented, Select, Space, Spin, Switch, Table, Tabs, Tag, Tooltip, Typography,
  message as antdMessage,
} from "antd";
import "@/designer/components/surface.css";
import { AppstoreOutlined, BarsOutlined, WarningOutlined } from "@ant-design/icons";
import { AlertTriangle, Boxes, Clock, Eye, GitBranch, Hash, Layers, ListChecks, PackageOpen, Pencil, Plus, Split, Tag as TagIcon } from "lucide-react";
import ReusableSearchInput from "@/components/ReusableSearch";
import DataTable from "@/components/DataTable";
import PageModal from "@/components/PageModal";
import Icons from "@/components/third-party/Icons";
import StatCard from "@/components/StatCard";
import { StepTypePicker, useVocabulary } from "@/designer/components";
import { getVocabulary, newKey, write } from "@/pages/Batch-Processing/mbmr/designerApi";
import { useTheme } from "@/App";
import { defaultColors } from "@/themes/antd-theme";

const { Text, Title } = Typography;

const pl = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/* What each coverage row looks like as a stat card: the same KPI cards every config page
   uses. Blocking rows are red, advisory amber, structure blue. */
const GAP_LOOK = {
  no_code: { label: "No code", icon: <Hash size={24} />, color: "#ef4444", bg: "#fee2e2" },
  no_phase: { label: "No ISA-88 phase", icon: <TagIcon size={24} />, color: "#ef4444", bg: "#fee2e2" },
  near_duplicate: { label: "Near-duplicate names", icon: <Split size={24} />, color: "#f59e0b", bg: "#fef3c7" },
  unused: { label: "Used by no record", icon: <PackageOpen size={24} />, color: "#f59e0b", bg: "#fef3c7" },
  stale: { label: (r) => r.label.replace("unused for over", "Unused over"), icon: <Clock size={24} />, color: "#f59e0b", bg: "#fef3c7" },
  generic_only: { label: "Generic with variants", icon: <GitBranch size={24} />, color: "#8b5cf6", bg: "#ede9fe" },
  no_rules: { label: "No rules declared", icon: <ListChecks size={24} />, color: "#f59e0b", bg: "#fef3c7" },
  split_headings: { label: "Headings spelled two ways", icon: <Split size={24} />, color: "#f59e0b", bg: "#fef3c7" },
  orphan_sub_operations: { label: "Orphan sub-operations", icon: <Boxes size={24} />, color: "#6b7280", bg: "#f3f4f6" },
};
const gapLabel = (r) => { const l = GAP_LOOK[r.key]?.label; return typeof l === "function" ? l(r) : (l || r.label); };

/** Coverage as the app's stat cards. The first two describe the library; the rest are
 *  what is wrong with it, and each is a filter. */
function CoverageStats({ coverage, templates, active, onPick }) {
  if (!coverage) return null;
  const col = { xs: 24, sm: 12, md: 8, lg: 6, xl: 4 };
  const wrap = (key, node, title) => (
    <Col key={key} {...col}>
      <Tooltip title={title} placement="bottom" mouseEnterDelay={0.4}>
        <div style={active === key ? { outline: "2px solid #1677ff", outlineOffset: 2, borderRadius: 12 } : undefined}>{node}</div>
      </Tooltip>
    </Col>
  );
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
      {wrap("total", <StatCard label="Operation types" value={coverage.total} icon={<Layers size={24} />} bgColor="#dbeafe" iconColor="#3b82f6" onClick={() => onPick(null)} />,
            `${coverage.total} types is a library. ${coverage.total + 1} is a fork: an author who cannot tell whether an existing type fits will make another one.`)}
      {wrap("templates", <StatCard label="With authored templates" value={templates} icon={<Boxes size={24} />} bgColor="#d1fae5" iconColor="#10b981" onClick={() => onPick(null)} />,
            "Types whose steps, repeats, decisions, stations and handover are authored in the designer and replayed onto every record built from them.")}
      {(coverage.rows || []).filter((r) => KPI_KEYS.includes(r.key)).map((r) => {
        const look = GAP_LOOK[r.key] || { icon: <AlertTriangle size={24} />, color: "#f59e0b", bg: "#fef3c7" };
        const filterable = !!(r.ids || []).length;
        return wrap(r.key,
          <StatCard label={gapLabel(r)} value={r.count} icon={look.icon} bgColor={look.bg} iconColor={look.color}
                    onClick={() => filterable && onPick(active === r.key ? null : r.key)} />,
          `${r.label}. ${r.why}${r.detail?.length ? ` (${r.detail.map((pair) => pair.join(" / ")).join(" · ")})` : ""}${filterable ? " Click to show these types." : ""}`);
      })}
    </Row>
  );
}

/* The KPI row is the four gaps an author acts on most; the rest stay one line below. */
const KPI_KEYS = ["no_phase", "no_code", "unused", "stale"];

/** The other coverage rows, as one line of counts — each still a filter. */
function MoreCoverage({ coverage, active, onPick }) {
  const rest = (coverage?.rows || []).filter((r) => !KPI_KEYS.includes(r.key));
  if (!rest.length) return null;
  return (
    <Flex wrap gap={14} align="center" style={{ margin: "-6px 0 14px", fontSize: 12.5 }}>
      <Text type="secondary" style={{ fontSize: 11, letterSpacing: ".08em" }}>ALSO</Text>
      {rest.map((r) => {
        const filterable = !!(r.ids || []).length;
        const detail = r.detail?.length ? ` (${r.detail.map((pair) => pair.join(" / ")).join(" · ")})` : "";
        return (
          <Tooltip key={r.key} title={`${r.label}. ${r.why}`} placement="bottom" mouseEnterDelay={0.4}>
            {filterable ? (
              <a onClick={() => onPick(active === r.key ? null : r.key)} style={{ fontWeight: active === r.key ? 600 : 400 }}>
                <b>{r.count}</b> {gapLabel(r).toLowerCase()}
              </a>
            ) : (
              <Text type="secondary"><b>{r.count}</b> {gapLabel(r).toLowerCase()}{detail}</Text>
            )}
          </Tooltip>
        );
      })}
    </Flex>
  );
}

/** One card per type: what it is, what it is made of, what uses it. */
function TypeCard({ t, onOpen, onEdit }) {
  const rules = Object.values(t.expects || {}).reduce((a, b) => a + b, 0);
  const kinds = (t.allowed_step_types || []).length;
  return (
    // A div, not a button: the card holds its own View / Edit buttons, and a button may not
    // hold another. Keyboard reach is kept with role, tabIndex and Enter / Space.
    <div role="button" tabIndex={0} className={`otl-card ${t.template ? "tpl" : ""}`} onClick={() => onOpen(t)}
         onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(t); } }}>
      <div className="otl-cardhead">
        {t.code ? <span className="otl-code">{t.code}</span> : <span className="otl-code none">no code</span>}
        <span className="otl-ver">v{t.version}</span>
      </div>
      <div className="otl-name">{t.name}</div>
      <div className="otl-tags">
        {t.critical ? <span className="otl-tag crit">critical</span> : null}
        {t.template ? (
          <Tooltip title={`Authored template, distilled from record ${t.template_from}: its repeats, decisions, stations and handover replay onto every record built from it.`}>
            <span className="otl-tag tpl"><Layers size={11} /> template</span>
          </Tooltip>
        ) : null}
        {t.specialises ? <span className="otl-tag fam" title={`A variant of ${t.specialises.name}`}>↳ {t.specialises.name}</span> : null}
        {t.variants ? <span className="otl-tag fam">{t.variants} variant{t.variants === 1 ? "" : "s"}</span> : null}
        {t.phase ? <span className="otl-tag">{t.phase}</span> : <span className="otl-tag faint">no phase</span>}
        {(t.classes || []).map((c) => <span key={c} className="otl-tag cls">{c}</span>)}
      </div>
      <div className="otl-stats">
        {/* What the type allows, not steps: steps are authored in a Standard Block. */}
        <span><b>{kinds || "—"}</b><small>{kinds === 1 ? "step type" : "step types"}</small></span>
        <span><b>{rules || "—"}</b><small>{rules === 1 ? "rule" : "rules"}</small></span>
        <span className={t.recipes ? "" : "unused"}><b>{t.recipes || "—"}</b><small>{t.recipes ? (t.recipes === 1 ? "record" : "records") : "unused"}</small></span>
      </div>
      <div className="otl-actions" onClick={(e) => e.stopPropagation()}>
        <Button size="small" icon={<Eye size={13} />} onClick={() => onOpen(t)}>View</Button>
        <Tooltip title={t.recipes ? `Every edit is a new version; the ${pl(t.recipes, "record")} built from it keep what they have until they update.` : "Every edit is a new version."}>
          <Button size="small" type="primary" icon={<Pencil size={13} />} onClick={() => onEdit(t)}>Edit</Button>
        </Tooltip>
      </div>
    </div>
  );
}

/** Types grouped by category, or by family (F13): a generic type and its variants together. */
function TypeCards({ types, onOpen, onEdit, by = "category" }) {
  if (!types.length) return <Empty description="Nothing matches." />;
  const groups = new Map();
  const byId = new Map(types.map((t) => [t.id, t]));
  for (const t of types) {
    let key;
    if (by === "family") {
      const head = t.specialises ? (byId.get(t.specialises.id)?.name || t.specialises.name) : (t.variants ? t.name : null);
      key = head ? `${head} family` : `Standalone · ${t.category || "Uncategorised"}`;
    } else {
      key = t.category || "Uncategorised";
    }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }
  const keys = [...groups.keys()].sort((a, b) =>
    (a.startsWith("Standalone") || a === "Uncategorised") - (b.startsWith("Standalone") || b === "Uncategorised") || a.localeCompare(b));
  return (
    <div className="otl-groups">
      {keys.map((k) => (
        <section key={k} className="otl-group">
          <header className="otl-grouphead">
            <span className="otl-groupname">{k}</span>
            <span className="otl-groupcount">{pl(groups.get(k).length, "type")}</span>
          </header>
          <div className="otl-cards">
            {groups.get(k)
              .sort((a, b) => (by === "family" ? (!!a.specialises - !!b.specialises) || a.name.localeCompare(b.name) : a.name.localeCompare(b.name)))
              .map((t) => <TypeCard key={t.id} t={t} onOpen={onOpen} onEdit={onEdit} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

/** F12 · a new type starts as an empty template, in the designer. Phase and code are
 *  required here so the library's two blocking gaps cannot grow (F11). */
function NewTypeModal({ open, onCancel, onCreated }) {
  const [vocab, setVocab] = useState({ phases: [], categories: [], classes: [] });
  const [form, setForm] = useState({ name: "", code: "", phase: undefined, category: undefined, classes: [], step_types: [] });
  // The designer's own vocabulary: the step types the new type may allow are the ones the
  // "What happens here?" picker offers, in the same groups.
  const stepVocab = useVocabulary(getVocabulary);
  // Every step type starts selected; the author narrows it to what the operation uses.
  useEffect(() => {
    if (open && stepVocab.step_types?.length) set("step_types", stepVocab.step_types.map((t) => t.value));
  }, [open, stepVocab]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    Axios.get("/bp/type-library/vocabulary/", { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } })
      .then((r) => setVocab(r.data || {})).catch(() => {});
  }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ok = form.name.trim() && form.code.trim() && form.phase;
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await Axios.post("/bp/type-library/create/", form, { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } });
      onCreated(r.data?.created);
    } catch (e) {
      setError(e?.response?.data?.message || "The type could not be created.");
    } finally { setBusy(false); }
  };
  return (
    <PageModal
      open={open}
      onCancel={onCancel}
      icon={<Icons.Plus size={22} />}
      title="New operation type"
      description="A new type starts as an empty template and opens in the designer."
      width={760}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={submit} loading={busy} disabled={!ok}>Create and open in the designer</Button>
        </div>
      }
    >
      <Flex vertical gap={10}>
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <div><span className="dz-eyebrow">Name</span>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={200} placeholder="What the operation is, in the plant's words" /></div>
        <Flex gap={10}>
          <div style={{ flex: 1 }}><span className="dz-eyebrow">Code — required</span>
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} maxLength={50} placeholder="OT-…" /></div>
          <div style={{ flex: 1 }}><span className="dz-eyebrow">ISA-88 phase — required</span>
            <Select style={{ width: "100%" }} value={form.phase} onChange={(v) => set("phase", v)} placeholder="Choose a phase"
                    options={(vocab.phases || []).map((p) => ({ value: p, label: p }))} /></div>
        </Flex>
        <Flex gap={10}>
          <div style={{ flex: 1 }}><span className="dz-eyebrow">Category</span>
            <Select style={{ width: "100%" }} allowClear showSearch value={form.category} onChange={(v) => set("category", v)} placeholder="Optional"
                    options={(vocab.categories || []).map((c) => ({ value: c, label: c }))} /></div>
          <div style={{ flex: 1 }}><span className="dz-eyebrow">Applies to</span>
            <Select style={{ width: "100%" }} mode="multiple" value={form.classes} onChange={(v) => set("classes", v)} placeholder="Any"
                    options={(vocab.classes || []).map((c) => ({ value: c, label: c }))} /></div>
        </Flex>
        <div><span className="dz-eyebrow">Step types this operation uses</span>
          <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Choose what happens in this operation. On a record, adding a step to an operation built from this type offers only these.
          </Text>
          <StepTypePicker vocab={stepVocab} value={form.step_types} onChange={(v) => set("step_types", v)} /></div>
        <Text type="secondary" style={{ fontSize: 12 }}>The type opens empty in the designer; its steps, repeats and decisions are authored there, and every edit is a version.</Text>
      </Flex>
    </PageModal>
  );
}

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem("Token")}` });

/** One operation type, in a modal: its own facts and the step types it allows. Opened from a
 *  card or a row — View reads it, Edit changes it as one version. Steps are not shown: an
 *  operation type says what kind of operation it is and what kinds of step it may hold; the
 *  steps themselves are authored in a Standard Block. */
function TypeDetailsModal({ typeId, startEditing, onClose, onSaved }) {
  const stepVocab = useVocabulary(getVocabulary);
  const [vocab, setVocab] = useState({ phases: [], categories: [], classes: [] });
  const [t, setT] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(!!startEditing);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fromType = (d) => ({
    name: d.name || "", code: d.code || "", phase: d.phase || undefined, category: d.category || undefined,
    classes: d.classes || [], description: d.description || "", step_types: d.allowed_step_types || [],
  });
  const load = async () => {
    const r = await Axios.get(`/bp/type-library/${typeId}/`, { headers: AUTH() });
    const d = r.data?.data;
    setT(d); setForm(d ? fromType(d) : null);
  };
  useEffect(() => {
    if (!typeId) return;
    setT(null); setForm(null); setError(null); setEditing(!!startEditing);
    load().catch(() => setError("This operation type could not be loaded."));
    Axios.get("/bp/type-library/vocabulary/", { headers: AUTH() }).then((r) => setVocab(r.data || {})).catch(() => {});
  }, [typeId, startEditing]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const stepGroups = useMemo(() => {
    const out = new Map();
    (stepVocab.step_types || []).forEach((x) => {
      const k = x.group || "Other";
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(x);
    });
    return [...out.entries()];
  }, [stepVocab]);
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const body = { ...form, phase: form.phase || "", category: form.category || "" };
      const res = await write("post", `/bp/type-library/${typeId}/details/`, body, { version: t.version, key: newKey() });
      if (res.ok) {
        antdMessage.open({ type: "success", content: res.changed?.length ? `Saved — ${res.changed.join(", ")}.` : "Nothing changed." });
        await load(); setEditing(false); onSaved?.();
      } else if (res.conflict) {
        setError("Someone else changed this type while you were editing. The latest version is now shown.");
        await load();
      } else setError(res.message || "The type could not be saved.");
    } finally { setBusy(false); }
  };

  return (
    <PageModal
      open={!!typeId}
      onCancel={onClose}
      width={900}
      destroyOnHidden
      icon={editing ? <Pencil size={22} /> : <Eye size={22} />}
      title={t ? t.name : "Operation type"}
      description={t ? (editing ? "Editing — saving records a new version." : "Operation type details") : undefined}
      meta={t ? (
        <>
          <Tag style={{ margin: 0 }}>v{t.version}</Tag>
          {editing ? <Tag color="gold" style={{ margin: 0 }}>Editing</Tag> : null}
        </>
      ) : null}
      footer={t ? (
        <div className="flex justify-end gap-2">
          {editing ? (
            <>
              <Button disabled={busy} onClick={() => { setForm(fromType(t)); setEditing(false); setError(null); }}>Cancel</Button>
              <Button type="primary" loading={busy} disabled={!form?.name?.trim() || !form?.code?.trim()} onClick={save}>Save</Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Close</Button>
              <Button type="primary" icon={<Pencil size={13} />} onClick={() => setEditing(true)}>Edit</Button>
            </>
          )}
        </div>
      ) : null}
    >
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 10 }} /> : null}
      {!t || !form ? (error ? null : <Spin />) : (
        <Flex vertical gap={12}>
          {editing ? (
            <>
              {t.recipe_count ? (
                <Alert type="info" showIcon message={`Used by ${pl(t.recipe_count, "record")}. Saving makes a new version; those records keep what they have until they update.`} />
              ) : null}
              <div><span className="dz-eyebrow">Name</span>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={200} /></div>
              <Flex gap={10}>
                <div style={{ flex: 1 }}><span className="dz-eyebrow">Code</span>
                  <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} maxLength={50} /></div>
                <div style={{ flex: 1 }}><span className="dz-eyebrow">ISA-88 phase</span>
                  <Select style={{ width: "100%" }} allowClear value={form.phase} onChange={(v) => set("phase", v)} placeholder="Choose a phase"
                    options={(vocab.phases || []).map((x) => ({ value: x, label: x }))} /></div>
              </Flex>
              <Flex gap={10}>
                <div style={{ flex: 1 }}><span className="dz-eyebrow">Category</span>
                  <Select style={{ width: "100%" }} allowClear showSearch value={form.category} onChange={(v) => set("category", v)} placeholder="Optional"
                    options={(vocab.categories || []).map((c) => ({ value: c, label: c }))} /></div>
                <div style={{ flex: 1 }}><span className="dz-eyebrow">Applies to</span>
                  <Select style={{ width: "100%" }} mode="multiple" value={form.classes} onChange={(v) => set("classes", v)} placeholder="Any"
                    options={(vocab.classes || []).map((c) => ({ value: c, label: c }))} /></div>
              </Flex>
              <div><span className="dz-eyebrow">Description</span>
                <Input.TextArea value={form.description} onChange={(e) => set("description", e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }} /></div>
            </>
          ) : (
            <Descriptions bordered size="middle" column={2}
              labelStyle={{ width: 150, fontWeight: 600, fontSize: 14 }}
              contentStyle={{ fontSize: 14 }}
              items={[
                { key: "name", label: "Name", children: t.name, span: 2 },
                { key: "code", label: "Code", children: t.code ? <Tag style={{ fontSize: 13 }}>{t.code}</Tag> : <Text type="danger">none</Text> },
                { key: "phase", label: "ISA-88 phase", children: t.phase || <Text type="secondary">not stated</Text> },
                { key: "category", label: "Category", children: t.category || <Text type="secondary">—</Text> },
                { key: "classes", label: "Applies to", children: (t.classes || []).length ? t.classes.map((c) => <Tag key={c} color="blue">{c}</Tag>) : "Any" },
                { key: "desc", label: "Description", children: t.description || <Text type="secondary">—</Text>, span: 2 },
                { key: "version", label: "Version", children: <>v{t.version}{t.version_reason ? <Text type="secondary" style={{ display: "block", fontSize: 12.5 }}>{t.version_reason}</Text> : null}</> },
                { key: "used", label: "Used by", children: pl(t.recipe_count || 0, "record") },
              ]} />
          )}
          <div>
            <Flex align="baseline" gap={10} style={{ marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>Step types this operation uses</Title>
              {!editing ? <Text type="secondary">{(t.allowed_step_types || []).length} of {(stepVocab.step_types || []).length}</Text> : null}
            </Flex>
            {editing ? (
              <StepTypePicker vocab={stepVocab} value={form.step_types} onChange={(v) => set("step_types", v)} disabled={busy} />
            ) : (t.allowed_step_types || []).length ? (
              /* Grouped as the step picker groups them, so the list reads the way it is chosen. */
              <div style={{ border: "1px solid var(--ant-color-border-secondary, #f0f0f0)", borderRadius: 8 }}>
                {stepGroups.map(([family, items]) => {
                  const on = items.filter((x) => t.allowed_step_types.includes(x.value));
                  if (!on.length) return null;
                  return (
                    <Flex key={family} gap={12} align="center" wrap={false}
                      style={{ padding: "10px 14px", borderBottom: "1px solid var(--ant-color-border-secondary, #f0f0f0)" }}>
                      <Text strong style={{ width: 190, flex: "none", fontSize: 13 }}>{family}</Text>
                      <Flex wrap gap={6}>
                        {on.map((x) => (
                          <Tag key={x.value} color="green" style={{ fontSize: 13.5, padding: "3px 10px", margin: 0 }}>{x.label}</Tag>
                        ))}
                      </Flex>
                    </Flex>
                  );
                })}
              </div>
            ) : <Text type="secondary">None chosen</Text>}
          </div>
        </Flex>
      )}
    </PageModal>
  );
}

export default function OperationTypeLibrary() {
  const { isDark } = useTheme();
  const textColor = isDark ? "#f3f4f6" : "#111827";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [facet, setFacet] = useState("All");
  const [gap, setGap] = useState(null);
  const [adding, setAdding] = useState(false);
  // The type open in the details modal, and whether it opened for editing.
  const [shown, setShown] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // F13: group by category or by family; show only authored templates.
  const [groupBy, setGroupBy] = useState(() => { try { return localStorage.getItem("otl.group") || "category"; } catch { return "category"; } });
  const pickGroup = (v) => { setGroupBy(v); try { localStorage.setItem("otl.group", v); } catch { /* private window */ } };
  const [templatesOnly, setTemplatesOnly] = useState(false);
  // Cards to browse by eye, the table to compare by column. Per-viewer, remembered.
  const [view, setView] = useState(() => { try { return localStorage.getItem("otl.view") || "cards"; } catch { return "cards"; } });
  const pickView = (v) => { setView(v); try { localStorage.setItem("otl.view", v); } catch { /* private window */ } };

  useEffect(() => {
    let alive = true;
    Axios.get("/bp/type-library/", {
      headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
    })
      .then((res) => alive && setData(res.data?.data || null))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const visible = useMemo(() => {
    let rows = data?.types || [];
    if (gap) {
      const row = (data.coverage.rows || []).find((r) => r.key === gap);
      const ids = new Set(row?.ids || []);
      rows = rows.filter((t) => ids.has(t.id));
    }
    if (facet !== "All") rows = rows.filter((t) => (t.classes || []).includes(facet));
    if (templatesOnly) rows = rows.filter((t) => t.template);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.code || "").toLowerCase().includes(q) ||
          (t.sub_operations || []).some((s) => s.name.toLowerCase().includes(q))
      );
    }
    return rows;
  }, [data, gap, facet, query, templatesOnly]);

  const columns = useMemo(() => [
    {
      title: "Code", dataIndex: "code", width: 150,
      render: (v) => (v ? <Text code>{v}</Text> : <Tag color="error">none</Tag>),
    },
    {
      title: "Operation type", dataIndex: "name", ellipsis: true,
      render: (v, t) => (
        <Space size={6}>
          <Text strong>{v}</Text>
          {t.critical ? <Tag color="red">CRITICAL</Tag> : null}
          {t.template ? <Tag color="geekblue" title={`Authored template, distilled from ${t.template_from}`}>template</Tag> : null}
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    { title: "Version", dataIndex: "version", width: 90, align: "right", render: (v) => `v${v}` },
    {
      title: "Phase", dataIndex: "phase", width: 130,
      render: (v) => (v ? <Tag>{v}</Tag> : <Text type="secondary">not stated</Text>),
    },
    { title: "Category", dataIndex: "category", width: 150, ellipsis: true },
    {
      title: "Applies to", dataIndex: "classes", width: 170,
      render: (v) => (v?.length ? v.join(", ") : <Text type="secondary">any</Text>),
    },
    {
      title: "Steps", width: 90, align: "right",
      render: (_, t) => (t.sub_operations?.length
        ? t.sub_operations.length
        : <Text type="secondary">none</Text>),
      sorter: (a, b) => a.sub_operations.length - b.sub_operations.length,
    },
    {
      title: "Expects", width: 130,
      render: (_, t) => {
        const n = Object.values(t.expects || {}).reduce((a, b) => a + b, 0);
        return n ? pl(n, "rule") : <Text type="secondary">nothing</Text>;
      },
    },
    {
      title: "Used by", width: 110, align: "right",
      render: (_, t) => (t.recipes ? pl(t.recipes, "record") : <Text type="secondary">no record</Text>),
      sorter: (a, b) => a.recipes - b.recipes,
      defaultSortOrder: "descend",
    },
    {
      title: "Action", key: "actions", width: 120, align: "center",
      render: (_, t) => (
        <div className="flex items-center justify-center gap-4" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View">
            <Icons.Eye onClick={() => setShown({ id: t.id, edit: false })} className="text-blue-600 w-6 h-6 cursor-pointer hover:text-blue-800" />
          </Tooltip>
          <Tooltip title="Edit">
            <Icons.Pencil onClick={() => setShown({ id: t.id, edit: true })} className="text-amber-600 w-6 h-6 cursor-pointer hover:text-amber-800" />
          </Tooltip>
        </div>
      ),
    },
  ], []);

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }
  if (!data) return <Empty description="The operation-type library could not be loaded." />;

  const activeGap = gap ? (data.coverage.rows || []).find((r) => r.key === gap) : null;
  const templates = (data.types || []).filter((t) => t.template).length;
  const facets = ["All", ...(data.facets || [])];

  return (
    <div className="p-2.5 pt-5 dz otl" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : "#f9fafb" }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : "#ffffff", minHeight: "calc(100vh - 120px)" }}
        title={
          <div className="flex items-center gap-2">
            <Layers className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>Operation Types</Title>
          </div>
        }
        extra={<HeaderActions>{
          <div className="flex items-center gap-2">
            <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setAdding(true)}>
              Add Operation Type
            </Button>
            <Segmented value={view} onChange={pickView}
                       options={[{ value: "cards", label: "Cards", icon: <AppstoreOutlined /> },
                                 { value: "table", label: "Table", icon: <BarsOutlined /> }]} />
            {view === "cards" && (
              <ReusableSearchInput
                placeholder="Search types, codes, steps..."
                allowClear
                style={{ width: 250 }}
                onSearch={(v) => setQuery(v)}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
          </div>
        }</HeaderActions>}
      >
        <CoverageStats coverage={data.coverage} templates={templates} active={gap} onPick={setGap} />
        <MoreCoverage coverage={data.coverage} active={gap} onPick={setGap} />

        {activeGap ? (
          <Alert
            type="info"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 12 }}
            message={`${activeGap.label}: ${activeGap.why}`}
            action={<Button size="small" onClick={() => setGap(null)}>Show all</Button>}
          />
        ) : null}

        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }} wrap gap={8}>
          <Text type="secondary" style={{ fontSize: 11, letterSpacing: ".08em" }}>
            {activeGap ? `MATCHING · ${activeGap.label.toUpperCase()}` : "ALL TYPES"}
          </Text>
          <Space size={12}>
            {/* what a type applies to is the first split an author makes — a filter beside the other list controls, not a third row of tabs */}
            <Space size={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>Class</Text>
            <Select
              size="small"
              value={facet}
              onChange={setFacet}
              style={{ width: 200 }}
              aria-label="Manufacturing class"
              options={facets.map((f) => ({
                value: f,
                label: `${f === "All" ? "All classes" : f} (${f === "All" ? (data.types || []).length : (data.types || []).filter((t) => (t.classes || []).includes(f)).length})`,
              }))}
            />
            </Space>
            {view === "cards" ? (
              <Space size={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>Group</Text>
                <Segmented size="small" value={groupBy} onChange={pickGroup}
                           options={[{ value: "category", label: "By category" }, { value: "family", label: "By family" }]} />
              </Space>
            ) : null}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
              <Switch size="small" checked={templatesOnly} onChange={setTemplatesOnly} /> templates only
            </label>
            <Text type="secondary" style={{ fontSize: 12 }}>{visible.length} shown</Text>
          </Space>
        </Flex>
        <NewTypeModal open={adding} onCancel={() => setAdding(false)}
          onCreated={(id) => { setAdding(false); setReloadKey((k) => k + 1); if (id) setShown({ id, edit: false }); }} />
        <TypeDetailsModal typeId={shown?.id} startEditing={shown?.edit} onClose={() => setShown(null)}
          onSaved={() => setReloadKey((k) => k + 1)} />

        {view === "cards" ? (
          <TypeCards types={visible} by={groupBy} onOpen={(t) => setShown({ id: t.id, edit: false })}
                     onEdit={(t) => setShown({ id: t.id, edit: true })} />
        ) : (
          <DataTable
            tableKey="operationTypes"
            searchable
            searchValue={query}
            onSearch={setQuery}
            searchPlaceholder="Search types, codes, steps..."
            columnConfig
            showCount={false}
            rowKey="id"
            size="small"
            bordered
            columns={columns}
            data={visible}
            scrollX="max-content"
            height={null}
            minHeight={240}
            emptyMessage="Nothing matches."
            onRow={(t) => ({
              onClick: () => setShown({ id: t.id, edit: false }),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </Card>
    </div>
  );
}
